import { useState, useEffect } from 'react'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import Input from '../UI/Input'
import Textarea from '../UI/Textarea'
import LoadingSpinner from '../UI/LoadingSpinner'
import { X, Shield } from 'lucide-react'

export default function AddPermissionModal({ isOpen, onClose, onSubmit, permission = null, isSubmitting = false }) {
  const isEditMode = !!permission
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    resource: '',
    action: '',
    active: true
  })
  const [errors, setErrors] = useState({})

  // Initialize form when modal opens or permission changes
  useEffect(() => {
    if (!isOpen) {
      // Reset when modal closes
      setFormData({
        name: '',
        description: '',
        resource: '',
        action: '',
        active: true
      })
      setErrors({})
      return
    }

    // Initialize form fields
    if (permission) {
      // Extract action from permissions object
      let action = '';
      if (permission.permissions && typeof permission.permissions === 'object') {
        // Handle new format: {"organizations.create": true}
        const permissionKeys = Object.keys(permission.permissions).filter(key => 
          permission.permissions[key] === true && key !== 'action'
        );
        if (permissionKeys.length > 0) {
          // Extract action from permission key (e.g., "organizations.create" -> "create")
          const parts = permissionKeys[0].split('.');
          action = parts.length > 1 ? parts[1] : '';
        } else if (permission.permissions.action) {
          // Handle old format: {"action": "create"}
          action = permission.permissions.action;
        }
      }
      
      setFormData({
        name: permission.name || '',
        description: permission.description || '',
        resource: permission.resource || '',
        action: action,
        active: permission.is_active !== false
      })
    } else {
      setFormData({
        name: '',
        description: '',
        resource: '',
        action: '',
        active: true
      })
    }
    setErrors({})
  }, [isOpen, permission?.id])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validate
    const newErrors = {}
    if (!formData.name || formData.name.trim().length < 2) {
      newErrors.name = 'Permission name is required and must be at least 2 characters'
    }
    if (!formData.resource || formData.resource.trim().length < 1) {
      newErrors.resource = 'Resource is required'
    }
    if (!formData.action || formData.action.trim().length < 1) {
      newErrors.action = 'Permission is required'
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    try {
      await onSubmit({
        name: formData.name.trim(),
        description: formData.description.trim(),
        resource: formData.resource.trim(),
        action: formData.action.trim(),
        active: formData.active
      })
    } catch (error) {
      console.error('Error submitting permission:', error)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        name: '',
        description: '',
        resource: '',
        action: '',
        active: true
      })
      setErrors({})
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose()
        }
      }}
    >
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Shield className="h-4 w-4 text-blue-600" />
            </div>
            <CardTitle>{isEditMode ? 'Edit Permission' : 'Add New Permission'}</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Permission Name */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Permission Name *
              </label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter permission name"
                disabled={isSubmitting}
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Description
              </label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter description"
                rows={3}
                disabled={isSubmitting}
              />
            </div>

            {/* Resource */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Resource *
              </label>
              <Input
                name="resource"
                value={formData.resource}
                onChange={handleInputChange}
                placeholder="e.g., organization, product, user"
                disabled={isSubmitting}
                className={errors.resource ? 'border-red-500' : ''}
              />
              {errors.resource && (
                <p className="mt-1 text-sm text-red-600">{errors.resource}</p>
              )}
            </div>

            {/* Permission (Action) */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Permission *
              </label>
              <select
                name="action"
                value={formData.action}
                onChange={handleInputChange}
                className={`input w-full ${errors.action ? 'border-red-500' : ''}`}
                disabled={isSubmitting}
              >
                <option value="">Select a permission</option>
                <option value="create">Create</option>
                <option value="read">Read</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="export">Export</option>
              </select>
              {errors.action && (
                <p className="mt-1 text-sm text-red-600">{errors.action}</p>
              )}
            </div>

            {/* Active Toggle */}
            <div className="flex items-center">
              <input
                type="checkbox"
                name="active"
                id="active"
                checked={formData.active}
                onChange={handleInputChange}
                disabled={isSubmitting}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="active" className="ml-2 block text-sm text-secondary-700">
                Active
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-secondary-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="min-w-[120px]"
              >
                {isSubmitting ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  isEditMode ? 'Update Permission' : 'Create Permission'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
